/**
 * /portal/playbook (T-045, PR-43, PR-44).
 *
 * Chronological feed of the subscriber's playbook entries with a tag filter
 * and a full-text search box. Search drives the Postgres GIN tsvector index
 * on content; the tag filter drives the GIN index on the tags array (both via
 * the supabase-js textSearch / contains operators in lib/portal). Empty-state
 * copy is the locked string from the AC.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { PlaybookEntry } from '@studio/api-schemas';
import { fetchOwnPlaybookEntries } from '@/lib/portal';
import { AsyncPanel, type AsyncState } from '@/components/portal/AsyncPanel';
import { TASK_TYPE_LABEL } from '@/components/portal/task-type-labels';

export const Route = createFileRoute('/portal/playbook')({
  component: PlaybookPage,
});

const EMPTY_COPY =
  'Once Eric delivers your first task, the playbook will start here.';

function PlaybookPage() {
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [state, setState] = useState<AsyncState<PlaybookEntry[]>>({
    status: 'loading',
  });
  // Keep an unfiltered copy so the tag chip set is stable across searches.
  const [allTags, setAllTags] = useState<string[]>([]);

  const load = useCallback(
    async (opts: { search?: string; tag?: string | null }) => {
      setState({ status: 'loading' });
      try {
        const entries = await fetchOwnPlaybookEntries({
          ...(opts.search ? { search: opts.search } : {}),
          ...(opts.tag ? { tag: opts.tag } : {}),
        });
        setState({ status: 'ready', data: entries });
        if (!opts.search && !opts.tag) {
          const tags = new Set<string>();
          entries.forEach((e) => e.tags.forEach((t) => tags.add(t)));
          setAllTags(Array.from(tags).sort());
        }
      } catch {
        setState({
          status: 'error',
          message: 'Could not load your playbook. Try again in a moment.',
        });
      }
    },
    [],
  );

  useEffect(() => {
    void load({});
  }, [load]);

  // Debounce search.
  useEffect(() => {
    const id = setTimeout(() => {
      void load({ search, tag: activeTag });
    }, 300);
    return () => clearTimeout(id);
  }, [search, activeTag, load]);

  const hasFilters = useMemo(
    () => search.trim().length > 0 || activeTag !== null,
    [search, activeTag],
  );

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
          Your playbook
        </p>
        <h1 className="mb-1 font-display text-h2 italic leading-tight text-[color:var(--color-navy)]">
          What you&apos;ve learned.
        </h1>
        <p className="max-w-xl text-sm leading-relaxed text-[color:var(--color-text-mute)]">
          Everything Eric has delivered, compounding. This is the library
          you&apos;ll use to brief colleagues, onboard AI agents, and stop
          starting from scratch. Search it or filter by tag.
        </p>
      </div>

      <div className="space-y-3">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search frameworks, topics, tags"
          aria-label="Search the playbook"
          className="w-full rounded-md border border-[color:var(--color-line-dark)] bg-[color:var(--color-white)] px-3.5 py-2.5 text-sm text-[color:var(--color-navy)] transition-colors focus:border-[color:var(--color-navy)] focus:outline-none focus:ring-2 focus:ring-[color:var(--color-rose)]"
        />
        {allTags.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            <FilterChip
              label="All"
              active={activeTag === null}
              onClick={() => setActiveTag(null)}
            />
            {allTags.map((t) => (
              <FilterChip
                key={t}
                label={t}
                active={activeTag === t}
                onClick={() => setActiveTag(activeTag === t ? null : t)}
              />
            ))}
          </div>
        ) : null}
      </div>

      <AsyncPanel state={state} loadingLabel="Loading your playbook...">
        {(entries) =>
          entries.length === 0 ? (
            <p className="text-sm text-[color:var(--color-text-mute)]">
              {hasFilters
                ? 'Nothing matches that. Clear the filter to see everything.'
                : EMPTY_COPY}
            </p>
          ) : (
            <ul className="space-y-3">
              {entries.map((e) => (
                <li
                  key={e.id}
                  className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-5"
                >
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <span className="inline-flex items-center rounded-sm bg-[color:var(--color-rose)] px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em] text-[color:var(--color-navy)]">
                      {e.task_type ? TASK_TYPE_LABEL[e.task_type] : 'Note'}
                    </span>
                    <time className="text-xs text-[color:var(--color-text-mute)]">
                      {new Date(e.created_at).toLocaleDateString('en-US')}
                    </time>
                  </div>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-[color:var(--color-navy)]">
                    {e.content}
                  </p>
                  {e.tags.length > 0 ? (
                    <div className="mt-4 flex flex-wrap gap-1.5">
                      {e.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-sm border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] px-2 py-0.5 text-[10px] text-[color:var(--color-text-mute)]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )
        }
      </AsyncPanel>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded-md border-[1.5px] px-3 py-1 text-xs font-medium transition-colors',
        active
          ? 'border-[color:var(--color-navy)] bg-[color:var(--color-navy)] text-[color:var(--color-linen)]'
          : 'border-[color:var(--color-line-dark)] bg-transparent text-[color:var(--color-text-mute)] hover:text-[color:var(--color-navy)]',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
